"use client";

import React, { createContext, useContext, useState, useCallback } from "react";

export type ToastType = "success" | "error" | "warning" | "info";

export interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration?: number;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (message: string, type?: ToastType, duration?: number) => void;
  removeToast: (id: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export const useToast = () => {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used within ToastProvider");
  return ctx;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, type: ToastType = "info", duration = 4000) => {
      const id = Math.random().toString(36).slice(2);
      setToasts((prev) => [...prev, { id, type, message, duration }]);
      setTimeout(() => removeToast(id), duration);
    },
    [removeToast]
  );

  return (
    <ToastContext.Provider value={{ toasts, showToast, removeToast }}>
      {children}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
    </ToastContext.Provider>
  );
};

/* ─── Toast Container & Individual Toast ─── */

const ICONS: Record<ToastType, React.ReactNode> = {
  success: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M20 6L9 17l-5-5" />
    </svg>
  ),
  error: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <circle cx="12" cy="12" r="10" />
      <path d="M15 9l-6 6M9 9l6 6" />
    </svg>
  ),
  warning: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  ),
  info: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5">
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="16" x2="12" y2="12" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </svg>
  ),
};

const STYLES: Record<ToastType, { bg: string; icon: string; bar: string; border: string }> = {
  success: {
    bg: "rgba(16, 185, 129, 0.08)",
    icon: "#10b981",
    bar: "linear-gradient(90deg, #10b981, #059669)",
    border: "rgba(16, 185, 129, 0.35)",
  },
  error: {
    bg: "rgba(239, 68, 68, 0.08)",
    icon: "#ef4444",
    bar: "linear-gradient(90deg, #ef4444, #dc2626)",
    border: "rgba(239, 68, 68, 0.35)",
  },
  warning: {
    bg: "rgba(245, 158, 11, 0.08)",
    icon: "#f59e0b",
    bar: "linear-gradient(90deg, #f59e0b, #d97706)",
    border: "rgba(245, 158, 11, 0.35)",
  },
  info: {
    bg: "rgba(184, 142, 47, 0.08)",
    icon: "#B88E2F",
    bar: "linear-gradient(90deg, #B88E2F, #8C6D23)",
    border: "rgba(184, 142, 47, 0.35)",
  },
};

const ToastContainer: React.FC<{ toasts: Toast[]; removeToast: (id: string) => void }> = ({
  toasts,
  removeToast,
}) => {
  if (toasts.length === 0) return null;
  return (
    <div
      style={{
        position: "fixed",
        top: "1.25rem",
        right: "1.25rem",
        zIndex: 99999,
        display: "flex",
        flexDirection: "column",
        gap: "0.75rem",
        pointerEvents: "none",
        maxWidth: "420px",
        width: "calc(100vw - 2.5rem)",
      }}
    >
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onClose={() => removeToast(toast.id)} />
      ))}
    </div>
  );
};

const ToastItem: React.FC<{ toast: Toast; onClose: () => void }> = ({ toast, onClose }) => {
  const [visible, setVisible] = React.useState(false);
  const s = STYLES[toast.type];
  const duration = toast.duration ?? 4000;

  React.useEffect(() => {
    // Trigger enter animation
    const t = setTimeout(() => setVisible(true), 10);
    return () => clearTimeout(t);
  }, []);

  const handleClose = () => {
    setVisible(false);
    setTimeout(onClose, 350);
  };

  return (
    <div
      onClick={handleClose}
      style={{
        pointerEvents: "all",
        cursor: "pointer",
        background: "rgba(255,255,255,0.97)",
        backdropFilter: "blur(16px)",
        WebkitBackdropFilter: "blur(16px)",
        border: `1px solid ${s.border}`,
        borderRadius: "16px",
        boxShadow:
          "0 8px 32px rgba(0,0,0,0.12), 0 2px 8px rgba(0,0,0,0.08), 0 0 0 1px rgba(255,255,255,0.6) inset",
        overflow: "hidden",
        transform: visible ? "translateX(0) scale(1)" : "translateX(120%) scale(0.9)",
        opacity: visible ? 1 : 0,
        transition: "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.35s ease",
        position: "relative",
      }}
    >
      {/* Coloured left accent */}
      <div
        style={{
          position: "absolute",
          left: 0,
          top: 0,
          bottom: 0,
          width: "4px",
          background: s.bar,
          borderRadius: "16px 0 0 16px",
        }}
      />

      <div style={{ display: "flex", alignItems: "flex-start", gap: "12px", padding: "14px 14px 14px 18px" }}>
        {/* Icon */}
        <div
          style={{
            flexShrink: 0,
            width: "36px",
            height: "36px",
            borderRadius: "10px",
            background: s.bg,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: s.icon,
          }}
        >
          {ICONS[toast.type]}
        </div>

        {/* Message */}
        <p
          style={{
            flex: 1,
            margin: 0,
            fontSize: "14px",
            fontWeight: 500,
            color: "#1a1a1a",
            lineHeight: "1.5",
            paddingTop: "8px",
            paddingRight: "8px",
            wordBreak: "break-word",
          }}
        >
          {toast.message}
        </p>

        {/* Close button */}
        <button
          onClick={(e) => { e.stopPropagation(); handleClose(); }}
          style={{
            flexShrink: 0,
            background: "transparent",
            border: "none",
            cursor: "pointer",
            color: "#9ca3af",
            padding: "4px",
            borderRadius: "6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "color 0.2s, background 0.2s",
            marginTop: "4px",
          }}
          onMouseEnter={(e) => {
            (e.currentTarget as HTMLElement).style.color = "#374151";
            (e.currentTarget as HTMLElement).style.background = "#f3f4f6";
          }}
          onMouseLeave={(e) => {
            (e.currentTarget as HTMLElement).style.color = "#9ca3af";
            (e.currentTarget as HTMLElement).style.background = "transparent";
          }}
          aria-label="Close notification"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" style={{ width: "14px", height: "14px" }}>
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      </div>

      {/* Progress bar */}
      <ProgressBar duration={duration} color={s.bar} />
    </div>
  );
};

const ProgressBar: React.FC<{ duration: number; color: string }> = ({ duration, color }) => {
  const [width, setWidth] = React.useState(100);

  React.useEffect(() => {
    // Small delay so CSS transition kicks in
    const t = setTimeout(() => setWidth(0), 50);
    return () => clearTimeout(t);
  }, []);

  return (
    <div style={{ height: "3px", background: "rgba(0,0,0,0.06)", position: "relative" }}>
      <div
        style={{
          height: "100%",
          background: color,
          width: `${width}%`,
          transition: `width ${duration - 100}ms linear`,
          borderRadius: "0 0 0 16px",
        }}
      />
    </div>
  );
};
